import React from 'react';
import { Link } from 'react-router-dom';

/* ────────────────────────────────────────────────────────────────────────────
   Inline SVG icons — we have no icon library installed, so we keep a small
   set of hand-rolled icons. All 24x24, currentColor, 1.6px stroke.
   ──────────────────────────────────────────────────────────────────────── */

type IconProps = { size?: number; className?: string; strokeWidth?: number };

const iconBaseProps = (size: number, strokeWidth: number, className?: string) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className,
  'aria-hidden': true,
});

/**
 * Chevrons and marks that were previously drawn with raw text glyphs — ⌄ → ←
 * × ‹ › ··· ✓ — sitting next to this hand-rolled SVG set. Text glyphs render in
 * the body font at the body baseline, so they never optically matched the
 * 1.6px-stroke icons they sat beside, and screen readers announced them.
 */
export const ChevronDownIcon = ({ size = 16, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const AlertIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5" />
    <path d="M12 16.2h.01" />
  </svg>
);

export const HomeIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5 10v10h14V10" />
    <path d="M10 20v-6h4v6" />
  </svg>
);

export const ChatIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M4 5h16v11H8l-4 3z" />
  </svg>
);

export const PersonIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

export const BodyIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <circle cx="12" cy="5" r="2.2" />
    <path d="M8 10h8l-1.5 6M12 10v12M9 22l1.5-6M15 22l-1.5-6" />
  </svg>
);

export const PulseIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M3 12h4l2-5 3 10 3-7 2 2h4" />
  </svg>
);

export const DocIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5" />
    <path d="M10 13h6M10 17h4" />
  </svg>
);

export const HistoryIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
    <path d="M3 4v5h5" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export const UploadIcon = ({ size = 16, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M12 16V4M7 9l5-5 5 5" />
    <path d="M4 18v2h16v-2" />
  </svg>
);

export const CalendarIcon = ({ size = 16, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

export const RefreshIcon = ({ size = 16, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 4v5h-5" />
  </svg>
);

export const MessageIcon = ({ size = 16, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M4 5h16v11H8l-4 3z" />
  </svg>
);

export const ChevronRightIcon = ({ size = 16, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="m9 6 6 6-6 6" />
  </svg>
);

export const ChevronLeftIcon = ({ size = 14, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="m15 6-6 6 6 6" />
  </svg>
);

export const FlaskIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M9 3h6" />
    <path d="M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" />
    <path d="M7.5 14h9" />
  </svg>
);

export const AppleIcon = ({ size = 20, className, strokeWidth = 1.6 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 6c0.4-2 2-3 3.6-3" />
    <path d="M12 6c.5-.4 1-.6 1.5-.6" />
    <path d="M9 7.5c-3 0-5 2.5-5 5.5 0 4 3 9 6 9 1 0 1.5-.5 2-.5s1 .5 2 .5c3 0 6-5 6-9 0-3-2-5.5-5-5.5-1 0-1.5.3-3 .3s-2-.3-3-.3z" />
  </svg>
);

export const PlusIcon = ({ size = 16, className, strokeWidth = 1.8 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

export const MinusIcon = ({ size = 16, className, strokeWidth = 1.8 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <path d="M5 12h14" />
  </svg>
);

export const LockIcon = ({ size = 14, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

export const SettingsIcon = ({ size = 18, className, strokeWidth = 1.6 }: IconProps) => (
  <svg {...iconBaseProps(size, strokeWidth, className)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

/* ────────────────────────────────────────────────────────────────────────────
   Breadcrumb + PageHeader
   Used at the top of every sub-page. Consistent breadcrumb "← Home / X".
   ──────────────────────────────────────────────────────────────────────── */

export function Breadcrumb({ trail }: { trail: Array<{ label: string; href?: string }> }) {
  return (
    <nav
      className="flex items-center gap-1.5 text-xs"
      style={{ color: 'var(--melio-text-muted)' }}
      aria-label="Breadcrumb"
    >
      <ChevronLeftIcon size={12} />
      {trail.map((item, idx) => {
        const isLast = idx === trail.length - 1;
        return (
          <React.Fragment key={item.label}>
            {item.href && !isLast ? (
              <Link to={item.href} className="hover:underline" style={{ color: 'var(--melio-olive-deep)' }}>
                {item.label}
              </Link>
            ) : (
              <span style={{ color: isLast ? 'var(--melio-ink)' : 'var(--melio-text-muted)' }}>{item.label}</span>
            )}
            {!isLast && <span aria-hidden style={{ color: 'var(--melio-text-muted)' }}>/</span>}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

interface PageHeaderProps {
  breadcrumb?: Array<{ label: string; href?: string }>;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  rightSlot?: React.ReactNode;
}

export function PageHeader({ breadcrumb, eyebrow, title, subtitle, rightSlot }: PageHeaderProps) {
  return (
    <header className="mb-6 md:mb-8">
      {breadcrumb && <div className="mb-3"><Breadcrumb trail={breadcrumb} /></div>}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {eyebrow && <p className="m-eyebrow mb-2">{eyebrow}</p>}
          <h1 className="m-display-1">{title}</h1>
          {subtitle && (
            <p className="text-sm md:text-base mt-2 max-w-2xl" style={{ color: 'var(--melio-text-muted)' }}>
              {subtitle}
            </p>
          )}
        </div>
        {rightSlot && <div className="shrink-0">{rightSlot}</div>}
      </div>
    </header>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   StatRow — 3 small cards shown under a page header (counts + tags).
   ──────────────────────────────────────────────────────────────────────── */

export interface StatRowItem {
  label: string;
  value: React.ReactNode;
  hint?: string;
}

export function StatRow({ items }: { items: StatRowItem[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
      {items.map((item) => (
        <div
          key={item.label}
          className="border px-4 py-3 bg-white melio-lift"
          style={{ borderColor: 'var(--m-line)' }}
        >
          <p className="m-eyebrow text-[10px] mb-1">{item.label}</p>
          <p className="m-title">{item.value}</p>
          {item.hint && <p className="text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>{item.hint}</p>}
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   TileCard + TileGrid — primary /home wayfinding.
   ──────────────────────────────────────────────────────────────────────── */

interface TileCardProps {
  icon: React.ReactNode;
  title: string;
  stat: string;
  href: string;
  accent?: 'olive' | 'sea' | 'terracotta' | 'sun' | 'oxblood';
}

/**
 * BRAND v3 "LEDGER". The accent names are kept — they have call sites across
 * the /home tiles — but repointed at the v3 palette, and the two raw hexes
 * (#8A4B3A, #8A6A24) that were in no token at all are gone. Tints are held at
 * a low alpha so the icon plate reads as a tinted square on a white cell,
 * not as a coloured button.
 */
const ACCENT_BG: Record<NonNullable<TileCardProps['accent']>, string> = {
  olive: 'rgba(79, 93, 24, 0.17)',       /* Botanical */
  sea: 'rgba(18, 35, 61, 0.14)',         /* Oxygen    */
  terracotta: 'rgba(94, 66, 24, 0.17)',  /* Earth     */
  sun: 'rgba(94, 66, 24, 0.17)',         /* Earth     */
  oxblood: 'rgba(61, 11, 13, 0.14)',     /* Oxblood   */
};

const ACCENT_FG: Record<NonNullable<TileCardProps['accent']>, string> = {
  olive: 'var(--b-botanical)',
  sea: 'var(--b-oxygen)',
  terracotta: 'var(--b-earth)',
  sun: 'var(--b-earth)',
  oxblood: 'var(--b-oxblood)',
};

export function TileCard({ icon, title, stat, href, accent = 'olive' }: TileCardProps) {
  return (
    <Link
      to={href}
      className="melio-lift border p-5 flex items-center gap-4 bg-white"
      style={{ borderColor: 'var(--m-line)' }}
    >
      <div
        aria-hidden
        className="shrink-0 flex items-center justify-center"
        style={{
          width: 48,
          height: 48,
          backgroundColor: ACCENT_BG[accent],
          color: ACCENT_FG[accent],
        }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="m-title-sm">{title}</p>
        <p className="text-xs mt-1 truncate" style={{ color: 'var(--melio-text-muted)' }}>{stat}</p>
      </div>
      <ChevronRightIcon size={16} className="shrink-0" />
    </Link>
  );
}

export function TileGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">{children}</div>;
}

/* ────────────────────────────────────────────────────────────────────────────
   ActionChip — pill chip button/link. Used in the /home add-ons row.
   ──────────────────────────────────────────────────────────────────────── */

interface ActionChipProps {
  icon?: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
}

export function ActionChip({ icon, label, href, onClick }: ActionChipProps) {
  const inner = (
    <span className="inline-flex items-center gap-2">
      {icon && <span style={{ color: 'var(--m-ink-muted)' }}>{icon}</span>}
      <span>{label}</span>
    </span>
  );
  const className =
    'inline-flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] border transition-colors hover:bg-white';
  const style = {
    borderColor: 'var(--m-line)',
    color: 'var(--m-ink)',
    backgroundColor: 'transparent',
  } as const;
  if (href) {
    return (
      <Link to={href} className={className} style={style}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} style={style}>
      {inner}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   SectionCard — the wrapping card we use for major page sections.
   ──────────────────────────────────────────────────────────────────────── */

interface SectionCardProps {
  eyebrow?: string;
  title?: string;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function SectionCard({ eyebrow, title, rightSlot, children, className = '' }: SectionCardProps) {
  return (
    <section
      className={`border p-5 md:p-6 bg-white ${className}`}
      style={{ borderColor: 'var(--m-line)' }}
    >
      {(eyebrow || title || rightSlot) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            {eyebrow && <p className="m-eyebrow text-[10px] mb-1">{eyebrow}</p>}
            {title && <h2 className="m-title">{title}</h2>}
          </div>
          {rightSlot && <div className="shrink-0">{rightSlot}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Shown when a read failed, in place of — never alongside — an empty state.
 *
 * The distinction it protects: "you have no reports yet" and "we could not read
 * your reports" look identical to a member if both render the empty state, and
 * only one of them is true. A member who has been told their record is empty
 * has been told something false about their own medical history, so this is a
 * correctness surface, not a cosmetic one.
 *
 * Shaped as a peer of SectionCard so it can be swapped in wherever a section
 * would have gone without disturbing the page rhythm.
 */
export function LoadFailureCard({
  title = 'We couldn’t load this just now',
  body = 'This is a connection problem, not a change to your record. Try again in a moment.',
  onRetry,
}: {
  title?: string;
  body?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="status"
      className="border p-5 md:p-6"
      style={{
        borderColor: 'var(--m-line)',
        borderLeft: '3px solid var(--m-attention)',
        backgroundColor: 'var(--m-surface)',
      }}
    >
      <div className="flex items-start gap-3">
        <span style={{ color: 'var(--m-attention)' }} aria-hidden="true">
          <AlertIcon size={20} />
        </span>
        <div className="min-w-0">
          <p className="font-medium mb-1" style={{ color: 'var(--m-ink)' }}>
            {title}
          </p>
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            {body}
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em]"
              style={{ backgroundColor: 'var(--m-accent)', color: 'var(--b-bone)' }}
            >
              <RefreshIcon size={14} /> Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
